import React, { useEffect, useMemo } from 'react';
import Loader from 'shared/Loader';
import { useParams } from 'react-router-dom';
import ContentShrink from 'shared/ContentShrink';
import { tableIds } from 'shared/Table/constants';
import { TableProvider } from 'shared/Table/TableContext';
import { useAppDispatch, useAppSelector } from 'core/store';
import { parseOpenapiSchema } from 'core/utilities/openapiSchema';
import { clearSalesSowData } from 'core/actions/Sales/GetSalesSow';
import { ISowDataListItem } from '@EngagementsSowData/sowData.model';
import getSalesSowAction from 'core/actions/Sales/GetSalesSow/actions';
import { getTableRowModeBoolean } from 'shared/Table/Actions/utilities';
import { getWidthToReduce } from 'pages/WirelineEngagements/shared/utilites';
import { useTableActiveRowContext } from 'shared/Table/TableActiveRowContext';
import { useUserPermissions } from '@EngagementsSowData/useSowDataPermissions';
import { getColumns, dynamicProductSchemaName } from '@EngagementsSowData/constants';
import { getColumnsFromMetaAndShchema } from 'core/utilities/getColumnsFromMetaAndShchema';
import { getSalesSowMetaAction, clearSalesSowMeta } from 'core/actions/Sales/GetSalesSowMeta';
import WirelineEngagementsSowDataTable from '@EngagementsSowData/WirelineEngagementsSowDataTable';
import BackToEditEngagementButton from 'pages/WirelineEngagements/shared/BackToEditEngagementButton';
import { isActiveNumbersItemCanceled } from 'pages/WirelineEngagements/WirelineEngagementsTables/utilities';

import 'pages/WirelineEngagements/WirelineEngagementsTables/WirelineEngagementsTables.scss';
import './WirelineEngagementsSowData.scss';

const WirelineEngagementsSowDataContainer = () => {
  const { customerId } = useParams();
  const dispatch = useAppDispatch();

  const {
    isAvailableEditOrder,
    isAvailableTableActions,
  } = useUserPermissions();

  const openApiSchemas = useAppSelector((state) => state.openapiSchemas.schemas);
  const metaColumns = useAppSelector((state) => state.wirelineEngagements.salesSowMeta.meta);
  const isLoadingMeta = useAppSelector((state) => state.wirelineEngagements.salesSowMeta.isLoading);
  const isLoadingSchemas = useAppSelector((state) => state.openapiSchemas.isLoading);
  const brandName = useAppSelector((state) => state.appVariables.data.brand);

  const retrieveSchema = useMemo(() => parseOpenapiSchema(
    openApiSchemas,
    dynamicProductSchemaName,
  ), [openApiSchemas],
  );

  const availableColumns = useMemo(
    () => getColumnsFromMetaAndShchema({
      metaColumns, columns: getColumns(brandName), retrieveSchema,
    }),
    [metaColumns, retrieveSchema],
  );

  const enableRowSelection = (
    isAvailableTableActions && ((row) => {
      const isCancaled = isActiveNumbersItemCanceled(row.original);

      return !isCancaled;
    })
  );

  const { activeRowMode, secondarySidebarMode } = useTableActiveRowContext<ISowDataListItem>();
  const { isEditMode, isViewMode, isCommentMode } = getTableRowModeBoolean(activeRowMode);

  const isTableShrank = isEditMode || isViewMode || isCommentMode;

  const getSalesSowMetaData = async () => { await dispatch(getSalesSowMetaAction()); };

  useEffect(() => {
    getSalesSowMetaData();

    return () => {
      dispatch(clearSalesSowMeta());
      dispatch(clearSalesSowData());
    };
  }, []);

  if (!customerId) {
    return null;
  }

  if (isLoadingMeta || isLoadingSchemas) {
    return <Loader />;
  }

  return (
    <div className="sow-data">
      <BackToEditEngagementButton
        title="SOW Data"
      />
      <ContentShrink
        isShrinked={isTableShrank}
        widthToReduce={getWidthToReduce(!!secondarySidebarMode)}
      >
        <TableProvider<string[]>
          id={tableIds.salesSow}
          columns={availableColumns}
          requestParams={[customerId]}
          getDataAction={getSalesSowAction}
          enableEditing={isAvailableEditOrder}
          enableRowSelection={enableRowSelection}
        >
          <WirelineEngagementsSowDataTable />
        </TableProvider>
      </ContentShrink>
    </div>
  );
};

export default WirelineEngagementsSowDataContainer;
